import { groupsApi } from '@/services/groupsApi'

const state = () => ({
  items: [],
  currentGroup: null,
  selectedGroup: null,
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    perPage: 15,
    total: 0
  },
  filters: {
    search: '',
    is_active: null
  }
})

const getters = {
  allGroups: (state) => state.items,
  activeGroups: (state) => state.items.filter(group => group.is_active),
  inactiveGroups: (state) => state.items.filter(group => !group.is_active),
  isLoading: (state) => state.loading,
  hasError: (state) => state.error,
  getCurrentGroup: (state) => state.currentGroup,
  getSelectedGroup: (state) => state.selectedGroup,
  getPagination: (state) => state.pagination,
  getFilters: (state) => state.filters
}

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  SET_ERROR(state, error) {
    state.error = error
  },
  
  SET_GROUPS(state, groups) {
    state.items = groups
  },
  
  SET_CURRENT_GROUP(state, group) {
    state.currentGroup = group
  },
  
  SET_SELECTED_GROUP(state, group) {
    state.selectedGroup = group
  },
  
  ADD_GROUP(state, group) {
    state.items.unshift(group)
  },
  
  UPDATE_GROUP(state, updatedGroup) {
    const index = state.items.findIndex(group => group.id === updatedGroup.id)
    if (index !== -1) {
      state.items.splice(index, 1, updatedGroup)
    }
  },
  
  REMOVE_GROUP(state, groupId) {
    state.items = state.items.filter(group => group.id !== groupId)
  },
  
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  
  SET_FILTERS(state, filters) {
    state.filters = { ...state.filters, ...filters }
  },
  
  CLEAR_ERROR(state) {
    state.error = null
  },
  
  CLEAR_CURRENT_GROUP(state) {
    state.currentGroup = null
  },
  
  CLEAR_SELECTED_GROUP(state) {
    state.selectedGroup = null
  }
}

const actions = {
  async fetchGroups({ commit, state }, options = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const params = {}
      
      if (options.all) {
        params.all = true
      } else {
        params.page = state.pagination.currentPage
        params.search = state.filters.search
        params.is_active = state.filters.is_active
      }
      
      const response = await groupsApi.getGroups(params)
      
      // Handle both direct response and nested data structure
      const responseData = response.data || response
      const groupsData = responseData.data || responseData
      
      commit('SET_GROUPS', groupsData)
      commit('SET_PAGINATION', {
        currentPage: responseData.current_page || 1,
        totalPages: responseData.last_page || 1,
        perPage: responseData.per_page || 15,
        total: responseData.total || groupsData.length
      })
      
      return { success: true, data: responseData }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch groups'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchGroup({ commit }, groupId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await groupsApi.getGroup(groupId)
      // Extract the actual group data from the response
      const groupData = response.data || response
      commit('SET_CURRENT_GROUP', groupData)
      
      return { success: true, data: groupData }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch group'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async createGroup({ commit }, groupData) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await groupsApi.createGroup(groupData)
      const createdGroup = response.data || response
      commit('ADD_GROUP', createdGroup)
      
      return { success: true, data: createdGroup }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create group'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async updateGroup({ commit, state }, { id, groupData }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await groupsApi.updateGroup(id, groupData)
      const updatedGroup = response.data || response
      commit('UPDATE_GROUP', updatedGroup)
      
      // Update current group if it's the one being edited
      if (state.currentGroup && state.currentGroup.id === id) {
        commit('SET_CURRENT_GROUP', updatedGroup)
      }
      
      return { success: true, data: updatedGroup }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update group'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async deactivateGroup({ commit }, groupId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await groupsApi.deactivateGroup(groupId)
      const updatedGroup = response.data || response
      commit('UPDATE_GROUP', updatedGroup)
      
      return { success: true, data: updatedGroup }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to deactivate group'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async restoreGroup({ commit }, groupId) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await groupsApi.restoreGroup(groupId)
      const updatedGroup = response.data || response
      commit('UPDATE_GROUP', updatedGroup)
      
      return { success: true, data: updatedGroup }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to restore group'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  setSelectedGroup({ commit }, group) {
    commit('SET_SELECTED_GROUP', group)
  },
  
  clearSelectedGroup({ commit }) {
    commit('CLEAR_SELECTED_GROUP')
  },
  
  async setPage({ commit, dispatch }, page) {
    commit('SET_PAGINATION', { currentPage: page })
    await dispatch('fetchGroups')
  },
  
  async setFilters({ commit, dispatch }, filters) {
    commit('SET_FILTERS', filters)
    commit('SET_PAGINATION', { currentPage: 1 })
    await dispatch('fetchGroups')
  },
  
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },
  
  clearCurrentGroup({ commit }) {
    commit('CLEAR_CURRENT_GROUP')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}